import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { BOARD_SCOPES, CLOSED_RESULTS, FLOWS, PRIORITIES, RISK_LEVELS, TABLERO_COLUMNS, type BoardCountsDTO, type BoardPageDTO, type BoardScope, type ClosedResult, type MissionStatus, type TableroColumn } from "@bgreen/shared";
import { useCallback, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { get } from "./api";

export type { BoardCardDTO, BoardCountsDTO, BoardPageDTO } from "@bgreen/shared";

/* ───────────── estado del tablero en la URL ───────────── */

export type BoardView = "board" | "list";
export interface BoardState {
  scope: BoardScope;
  view: BoardView;
  /** Columna del Tablero (fases y «Completadas recientes»): pestaña en móvil y filtro de columna en la lista. */
  col: TableroColumn | "";
  /** Solo en Cerradas: pestaña de resultado en móvil y filtro de resultado en la lista. */
  result: ClosedResult | "";
  q: string;
  project: string;
  repo: string;
  prio: string;
  risk: string;
  flow: string;
  agent: "running" | "idle" | "";
  mine: boolean;
}

export const EMPTY_STATE: BoardState = { scope: "board", view: "board", col: "", result: "", q: "", project: "", repo: "", prio: "", risk: "", flow: "", agent: "", mine: false };
/** Filtros «secundarios» (van en «Más filtros»). */
export const SECONDARY_KEYS = ["prio", "risk", "flow", "agent", "mine"] as const;
const oneOf = <T extends string>(v: string | null, xs: readonly T[]): T | "" => (v && (xs as readonly string[]).includes(v) ? (v as T) : "");

export function readState(sp: URLSearchParams): BoardState {
  const scope = oneOf(sp.get("scope"), BOARD_SCOPES) || "board"; // un `scope=active` antiguo (o cualquier valor desconocido) cae en el Tablero
  const col = oneOf(sp.get("col"), TABLERO_COLUMNS);
  return {
    scope,
    view: sp.get("view") === "list" ? "list" : "board",
    col: scope === "attention" && col === "recent" ? "" : col, // «Necesita mi intervención» nunca incluye completadas
    result: oneOf(sp.get("result"), CLOSED_RESULTS),
    q: (sp.get("q") ?? "").slice(0, 100),
    project: sp.get("project") ?? "",
    repo: sp.get("repo") ?? "",
    prio: oneOf(sp.get("prio"), PRIORITIES),
    risk: oneOf(sp.get("risk"), RISK_LEVELS),
    flow: oneOf(sp.get("flow"), FLOWS),
    agent: oneOf(sp.get("agent"), ["running", "idle"] as const),
    mine: sp.get("mine") === "1",
  };
}

/** Solo escribe lo que difiere del valor por defecto: URLs cortas y compartibles. */
export function writeState(s: BoardState): URLSearchParams {
  const p = new URLSearchParams();
  (Object.keys(EMPTY_STATE) as (keyof BoardState)[]).forEach((k) => {
    const v = s[k];
    if (v === EMPTY_STATE[k] || v === "" || v === false) return;
    p.set(k, v === true ? "1" : String(v));
  });
  return p;
}

export const activeFilterCount = (s: BoardState) => [s.q, s.project, s.repo, s.prio, s.risk, s.flow, s.agent, s.mine, s.scope === "closed" && s.view === "list" ? s.result : ""].filter(Boolean).length; // en las columnas de Cerradas el resultado es una pestaña, no un filtro
export const secondaryCount = (s: BoardState) => SECONDARY_KEYS.filter((k) => !!s[k]).length;

const LAST_KEY = "bgf.board.last";
const OPENED_KEY = "bgf.board.opened";
const store = {
  get: (k: string) => { try { return sessionStorage.getItem(k); } catch { return null; } },
  set: (k: string, v: string) => { try { sessionStorage.setItem(k, v); } catch { /* sin almacenamiento */ } },
  del: (k: string) => { try { sessionStorage.removeItem(k); } catch { /* sin almacenamiento */ } },
};
export const rememberOpened = (id: string) => store.set(OPENED_KEY, id);
export const peekOpened = () => store.get(OPENED_KEY);
export const clearOpened = () => store.del(OPENED_KEY);

/**
 * La URL es la fuente de verdad (vista, filtros, fase). Al abrir /missions sin parámetros se recupera lo último
 * usado en esta pestaña: así «← Misiones» desde un detalle devuelve al mismo tablero filtrado. «Limpiar» lo vacía.
 */
export function useBoardState() {
  const [sp, setSp] = useSearchParams();
  const state = useMemo(() => readState(sp), [sp]);
  useEffect(() => {
    if (sp.toString() === "") {
      const last = store.get(LAST_KEY);
      if (last) setSp(new URLSearchParams(last), { replace: true });
    } else store.set(LAST_KEY, sp.toString());
  }, [sp, setSp]);
  const update = useCallback((patch: Partial<BoardState>) => {
    const next = writeState({ ...readState(sp), ...patch });
    store.set(LAST_KEY, next.toString());
    setSp(next, { replace: true });
  }, [sp, setSp]);
  const reset = useCallback((keep: Partial<BoardState> = {}) => update({ ...EMPTY_STATE, ...keep }), [update]);
  return { state, update, reset };
}

/* ───────────── consultas ───────────── */

/** Parámetros de la API a partir del estado. Se usan también como clave de caché. */
export function filterParams(s: BoardState, companyId: string | null, meId: string | undefined): URLSearchParams {
  const p = new URLSearchParams();
  if (companyId) p.set("companyId", companyId);
  if (s.project) p.set("projectId", s.project);
  if (s.repo) p.set("repositoryId", s.repo);
  if (s.q.trim()) p.set("q", s.q.trim());
  if (s.prio) p.set("priority", s.prio);
  if (s.risk) p.set("risk", s.risk);
  if (s.flow) p.set("flow", s.flow);
  if (s.agent) p.set("agent", s.agent);
  if (s.mine && meId) p.set("assigneeId", meId);
  return p;
}

const REFRESH_MS = 15_000;

export function useBoardCounts(filters: URLSearchParams) {
  const key = filters.toString();
  return useQuery({ queryKey: ["board", "counts", key], queryFn: () => get<BoardCountsDTO>(`/board/counts?${key}`), refetchInterval: REFRESH_MS, placeholderData: (prev) => prev });
}

export function useBoardPages(filters: URLSearchParams, o: { scope: BoardScope; column?: TableroColumn | ""; result?: ClosedResult | ""; enabled?: boolean; limit?: number }) {
  const key = filters.toString();
  return useInfiniteQuery({
    queryKey: ["board", "page", key, o.scope, o.column ?? "", o.result ?? "", o.limit ?? 25],
    initialPageParam: "" as string,
    queryFn: ({ pageParam }) => {
      const p = new URLSearchParams(key);
      p.set("scope", o.scope);
      if (o.column && o.scope !== "closed") p.set("column", o.column);
      if (o.result && o.scope === "closed") p.set("result", o.result);
      p.set("limit", String(o.limit ?? 25));
      if (pageParam) p.set("cursor", pageParam);
      return get<BoardPageDTO>(`/board/missions?${p.toString()}`);
    },
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: o.enabled ?? true,
    refetchInterval: REFRESH_MS,
    placeholderData: (prev) => prev,
  });
}

/* ───────────── vistas guardadas (solo en este navegador) ───────────── */

export interface SavedView { id: string; name: string; params: string }
const VIEWS_KEY = "bgf.board.views.v1";
export function loadViews(): SavedView[] {
  try {
    const v = JSON.parse(localStorage.getItem(VIEWS_KEY) ?? "[]") as unknown;
    return Array.isArray(v) ? v.filter((x): x is SavedView => !!x && typeof x.id === "string" && typeof x.name === "string" && typeof x.params === "string").slice(0, 12) : [];
  } catch { return []; }
}
export function persistViews(v: SavedView[]) { try { localStorage.setItem(VIEWS_KEY, JSON.stringify(v.slice(0, 12))); } catch { /* sin almacenamiento */ } }

/* ───────────── presentación ───────────── */

export const STATE_SHORT: Record<MissionStatus, string> = {
  pending: "Pendiente", planning: "Planificando", awaiting_plan_approval: "Plan listo", developing: "Programando", awaiting_ci: "Esperando CI", in_review: "Entrega lista",
  merging: "Esperando merge", done: "Terminada", blocked: "Bloqueada", error: "Con error", awaiting_budget: "Sin presupuesto", cancelled: "Cancelada",
};

/** Duración corta y legible: «12 min», «3 h», «2 d». */
export function span(iso: string | null | undefined, now = Date.now()): string | null {
  if (!iso) return null;
  const m = Math.max(0, Math.floor((now - new Date(iso).getTime()) / 60_000));
  if (m < 1) return "<1 min";
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h} h`;
  return `${Math.floor(h / 24)} d`;
}
